import { PAGE_ROUTES } from '@shared/core-data';
import { MenuItemInterface } from '@ui-kit';
import { Injectable, inject } from '@angular/core';
import { UserService } from '@shared/auth';
import { PermissionService } from '@shared/auth';
import { Permission } from '@shared/data-access';

@Injectable()
export class MenuItemService {
  private userService = inject(UserService);
  private permissionService = inject(PermissionService);

  getMenuItems(): MenuItemInterface[] {
    return [
      {
        link: `/${PAGE_ROUTES.DASHBOARD}`,
        icon: 'element-11',
        titleKey: 'breadcrumbs.dashboard',
        locked: !this.permissionService.hasPermission(
          Permission.DASHBOARD_VIEW_NAV_BAR,
        ),
      },
      {
        link: `/${PAGE_ROUTES.LOCATIONS}`,
        icon: 'geolocation',
        titleKey: 'breadcrumbs.locations',
        locked: !this.permissionService.hasPermission(
          Permission.LOCATIONS_VIEW_NAV_BAR,
        ),
        subItems: this.getLocationSubItems(),
      },
      {
        link: `/${PAGE_ROUTES.CUSTOMERS}`,
        icon: 'people',
        titleKey: 'breadcrumbs.customers',
        locked: !this.permissionService.hasPermission(
          Permission.CUSTOMERS_VIEW_NAV_BAR,
        ),
        subItems: this.getCustomerSubItems(),
      },
      {
        link: `/${PAGE_ROUTES.EVENTS}`,
        icon: 'abstract-3',
        titleKey: 'breadcrumbs.events',
        adminCredentials: !this.userService.isAdmin(),
        locked: !this.permissionService.hasPermission(
          Permission.EVENTS_VIEW_NAV_BAR,
        ),
      },
      {
        link: `/${PAGE_ROUTES.CAMPAIGNS}`,
        icon: 'rocket',
        titleKey: 'breadcrumbs.campaigns',
        locked: !this.permissionService.hasPermission(
          Permission.CAMPAIGNS_VIEW_NAV_BAR,
        ),
      },
      {
        link: `/${PAGE_ROUTES.OFFERS}`,
        icon: 'book-open',
        titleKey: 'breadcrumbs.offers',
        adminCredentials: this.userService.isAdmin(),
        locked: !this.permissionService.hasPermission(
          Permission.OFFERS_VIEW_NAV_BAR,
        ),
      },
      {
        link: `/${PAGE_ROUTES.ADMIN_OFFERS}`,
        icon: 'book-open',
        titleKey: 'breadcrumbs.offers',
        adminCredentials: !this.userService.isAdmin(),
        locked: !this.permissionService.hasPermission(
          Permission.OFFERS_VIEW_NAV_BAR,
        ),
      },
      {
        link: `/${PAGE_ROUTES.MARKETS}`,
        icon: 'shop',
        titleKey: 'breadcrumbs.markets',
        adminCredentials: this.userService.isAdmin(),
      },
      {
        link: `/${PAGE_ROUTES.PRODUCTS}`,
        icon: 'package',
        titleKey: 'breadcrumbs.products',
      },
      {
        link: `/${PAGE_ROUTES.BRANDS}`,
        icon: 'lots-shopping',
        titleKey: 'breadcrumbs.brands',
        locked: !this.permissionService.hasPermission(
          Permission.BRANDS_VIEW_NAV_BAR,
        ),
        subItems: this.getBrandSubItems(),
      },
      {
        link: `/${PAGE_ROUTES.MARKET_MANAGEMENT}`,
        icon: 'map',
        titleKey: 'breadcrumbs.marketManagement',
        adminCredentials: !this.userService.isAdmin(),
      },
      {
        link: `/${PAGE_ROUTES.MANAGE_USERS}`,
        icon: 'profile-user',
        titleKey: 'breadcrumbs.manageUsers',
        adminCredentials: !this.userService.isAccountOwner(),
        locked: !this.permissionService.hasPermission(
          Permission.ORGANIZATIONS_VIEW_NAV_BAR,
        ),
        subItems: [
          {
            link: `${PAGE_ROUTES.MANAGE_USERS + '/' + PAGE_ROUTES.USERS}`,
            icon: 'user-square',
            titleKey: 'breadcrumbs.users',
            locked: !this.permissionService.hasPermission(
              Permission.ORGANIZATIONS_VIEW_GRID_MEMBERS,
            ),
          },
          {
            link: `${PAGE_ROUTES.MANAGE_USERS + '/' + PAGE_ROUTES.ROLES}`,
            icon: 'abstract-28',
            titleKey: 'breadcrumbs.roles',
          },
        ],
      },
      {
        link: `/${PAGE_ROUTES.MARKETING}`,
        icon: 'data',
        titleKey: 'breadcrumbs.marketing',
      },
      {
        link: `/${PAGE_ROUTES.ORGANIZATION_CONFIGURATION}`,
        icon: 'message-edit',
        titleKey: 'breadcrumbs.organizationConfiguration',
        adminCredentials: !this.userService.isAdmin(),
        subItems: [
          {
            link: `${PAGE_ROUTES.ORGANIZATION_CONFIGURATION + '/' + PAGE_ROUTES.AMBASSADORS}`,
            icon: 'user-tick',
            titleKey: 'breadcrumbs.ambassadors',
          },
          {
            link: `${PAGE_ROUTES.ORGANIZATION_CONFIGURATION + '/' + PAGE_ROUTES.VENDORS}`,
            icon: 'people',
            titleKey: 'breadcrumbs.vendors',
          },
        ],
      },
      {
        link: `/${PAGE_ROUTES.NOTIFICATIONS_CENTER}`,
        icon: 'notification-on',
        titleKey: 'breadcrumbs.notificationsCenter',
        adminCredentials: !this.userService.isAdmin(),
        subItems: [
          {
            link: `${PAGE_ROUTES.NOTIFICATIONS_CENTER + '/' + PAGE_ROUTES.ACCOUNTS}`,
            icon: 'profile-user',
            titleKey: 'breadcrumbs.organizations',
          },
          {
            link: `${PAGE_ROUTES.NOTIFICATIONS_CENTER + '/' + PAGE_ROUTES.ADMIN_OFFERS}`,
            icon: 'book-open',
            titleKey: 'breadcrumbs.offers',
          },
          {
            link: `${PAGE_ROUTES.NOTIFICATIONS_CENTER + '/' + PAGE_ROUTES.BRANDS}`,
            icon: 'lots-shopping',
            titleKey: 'breadcrumbs.brands',
          },
          {
            link: `${PAGE_ROUTES.NOTIFICATIONS_CENTER + '/' + PAGE_ROUTES.LINK_LOCATIONS}`,
            icon: 'geolocation',
            titleKey: 'breadcrumbs.linkLocations',
          },
          {
            link: `${PAGE_ROUTES.NOTIFICATIONS_CENTER + '/' + PAGE_ROUTES.CREATE_LOCATIONS}`,
            icon: 'flash-circle',
            titleKey: 'breadcrumbs.locationsCreate',
          },
          {
            link: `${PAGE_ROUTES.NOTIFICATIONS_CENTER + '/' + PAGE_ROUTES.EDIT_LOCATIONS}`,
            icon: 'compass',
            titleKey: 'breadcrumbs.locationsEdit',
          },
          {
            link: `${PAGE_ROUTES.NOTIFICATIONS_CENTER + '/' + PAGE_ROUTES.EDIT_BRANDS}`,
            icon: 'lots-shopping',
            titleKey: 'breadcrumbs.brandsEdit',
          },
          {
            link: `${PAGE_ROUTES.NOTIFICATIONS_CENTER + '/' + PAGE_ROUTES.MARKETS}`,
            icon: 'shop',
            titleKey: 'breadcrumbs.markets',
          },
        ],
      },
      {
        link: `/${PAGE_ROUTES.CONFIGURATION}`,
        icon: 'wrench',
        titleKey: 'breadcrumbs.configuration',
        adminCredentials: !this.userService.isAdmin(),
        subItems: [
          {
            link: `${PAGE_ROUTES.CONFIGURATION + '/' + PAGE_ROUTES.AMBASSADOR_CODES}`,
            icon: 'paintbucket',
            titleKey: 'breadcrumbs.ambassadorCodes',
          },
        ],
      },
      {
        href: 'https://login.clearline.me/',
        image: 'assets/media/clearline-logo.svg',
        titleKey: 'breadcrumbs.clearLine',
      },
    ];
  }

  private getLocationSubItems(): MenuItemInterface['subItems'] {
    const isMerchant = this.userService.isMerchantOwner();
    const isBrand = this.userService.isBrandOwner();

    if (!isMerchant && !isBrand) {
      return undefined;
    }

    const subItems: NonNullable<MenuItemInterface['subItems']> = [
      {
        link: `${PAGE_ROUTES.LOCATIONS}`,
        icon: 'pointers',
        titleKey: 'breadcrumbs.allLocations',
        routerLinkActiveOptions: { exact: true },
      },
      {
        link: `${PAGE_ROUTES.LOCATIONS + '/' + PAGE_ROUTES.LINK}`,
        icon: 'flag',
        titleKey: 'breadcrumbs.linkLocations',
      },
    ];

    if (isMerchant) {
      subItems.push(
        {
          link: `${PAGE_ROUTES.LOCATIONS + '/' + PAGE_ROUTES.CREATE}`,
          icon: 'flash-circle',
          titleKey: 'breadcrumbs.locationsCreate',
        },
        {
          link: `${PAGE_ROUTES.LOCATIONS + '/' + PAGE_ROUTES.EDIT}`,
          icon: 'compass',
          titleKey: 'breadcrumbs.locationsEdit',
        },
      );
    }

    return subItems;
  }

  private getCustomerSubItems(): MenuItemInterface['subItems'] {
    if (this.userService.isAdmin()) {
      return undefined;
    }

    return [
      {
        link: `${PAGE_ROUTES.CUSTOMERS + '/' + PAGE_ROUTES.PROMOS}`,
        icon: 'discount',
        titleKey: 'breadcrumbs.promoCustomers',
        locked: !this.permissionService.hasPermission(
          Permission.CUSTOMERS_VIEW_GRID,
        ),
      },
    ];
  }

  private getBrandSubItems(): MenuItemInterface['subItems'] {
    const isMerchant = this.userService.isMerchantOwner();
    const isAdmin = this.userService.isAdmin();
    const isAmbassador = this.userService.isAmbassadorOwner();

    if (isMerchant || isAdmin || isAmbassador) {
      return undefined;
    }

    return [
      {
        link: `${PAGE_ROUTES.BRANDS}`,
        icon: 'lots-shopping',
        titleKey: 'breadcrumbs.allBrands',
        routerLinkActiveOptions: { exact: true },
      },
      {
        link: `${PAGE_ROUTES.BRANDS + '/' + PAGE_ROUTES.EDIT_BRANDS}`,
        icon: 'pencil',
        titleKey: 'breadcrumbs.brandsEdit',
      },
    ];
  }
}
